import React, { useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';
import DocumentDownloadService from '../../../../utils/documentDownloadService';
import { useGetReturnProcessDocumentsByIdQuery } from '../../../../api/endpoints/dealerPartsReturnProcess';
import { useLazyDownloadDocumentByKeyQuery } from '../../../../api/endpoints/documentContent';
import { formatDateOnly } from '../../../../utils/dateUtils';

const DealerPartsDetailView = ({ data, activeIndex, onSendToRM, isSendingToRM, onCancelRequest, isCancellingRequest, onApproveRequest, isApproving, onRejectRequest, isRejecting }) => {
    const { th } = useLanguage();
    const { hasDealerRole, hasWarrantyTeamRole, hasKarsanLogisticsRole, hasAdminRole } = usePermissions();

    // İade Talep Durumu için status mapping
    const getReturnRequestStatusText = (status) => {
        if (status == null) return '-';
        const statusKey = `dealerPartsReturnProcess.detailView.returnRequestStatusValues.${status}`;
        const translated = th(statusKey);
        // Eğer çeviri bulunamazsa (key döndürülürse), numeric değeri göster
        return translated !== statusKey ? translated : status;
    };

    // Toast ve document download service
    const toastRef = useRef(null);
    const [documentDownloadService] = useState(() => new DocumentDownloadService(toastRef));

    // Dosya indirme için RTK Query hooks
    const [triggerDownloadByKey, { isLoading: isDownloadingDocument }] = useLazyDownloadDocumentByKeyQuery();

    // Dökümanları fetch et - sadece returnRequestId varsa
    const {
        data: documentsResponse,
        isLoading: isLoadingDocuments,
        error: documentsError
    } = useGetReturnProcessDocumentsByIdQuery(data?.returnRequestId, {
        skip: !data?.returnRequestId
    });

    // API'den gelen dökümanları transform et
    const documentsData = documentsResponse?.data || [];

    // Dosya indirme işlemi
    const handleDownloadDocument = async (documentData, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        try {
            if (documentDownloadService.toastRef !== toastRef) {
                documentDownloadService.toastRef = toastRef;
            }

            await documentDownloadService.downloadByKey(triggerDownloadByKey, documentData.fileKey, documentData, {
                successMessage: 'Dosya başarıyla indirildi.',
                errorMessage: 'Dosya indirilemedi.'
            });
        } catch (error) {
            console.error('Dosya indirme hatası:', error);
        }
    };

    if (!data) {
        return <div className="text-center p-4">{th('common.noDataAvailable')}</div>;
    }

    return (
        <>
            <Toast ref={toastRef} />
            <div className="p-fluid">
                {/* Parça Bilgileri ve Ambar & Şube Bilgileri - Yan Yana */}
                <div className="grid">
                    {/* Sol Kolon - Parça Bilgileri */}
                    <div className="col-12 lg:col-6">
                        <div>
                            <div className="card-header" style={{ background: '#273A81', color: 'white', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0' }}>
                                <div className="flex align-items-center">
                                    <i className="pi pi-cog mr-3" style={{ fontSize: '1.25rem' }}></i>
                                    <h6 className="m-0 font-semibold" style={{ color: 'white' }}>
                                        {th('dealerPartsReturnProcess.detailView.partInformationTitle')}
                                    </h6>
                                </div>
                            </div>
                            <div className="card-body" style={{ padding: '0.5rem' }}>
                                <div className="grid">
                                    <div className="col-12">
                                        <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef', marginBottom: '0.3rem' }}>
                                            <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                                <i className="pi pi-tag mr-2" style={{ color: '#5D72B2' }}></i>
                                                {th('dealerPartsReturnProcess.partCode')}
                                            </label>
                                            <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0' }}>
                                                {data.partCode || '-'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef', marginBottom: '0.3rem' }}>
                                            <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                                <i className="pi pi-info-circle mr-2" style={{ color: '#5D72B2' }}></i>
                                                {th('dealerPartsReturnProcess.partDesc')}
                                            </label>
                                            <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0' }}>
                                                {data.partDesc || '-'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                            <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                                <i className="pi pi-chart-bar mr-2" style={{ color: '#5D72B2' }}></i>
                                                {th('dealerPartsReturnProcess.uom')}
                                            </label>
                                            <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0' }}>
                                                {data.uom || '-'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                            <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                                <i className="pi pi-hashtag mr-2" style={{ color: '#5D72B2' }}></i>
                                                {th('dealerPartsReturnProcess.quantity')}
                                            </label>
                                            <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0' }}>
                                                {data.quantity || '-'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sağ Kolon - Ambar ve Şube Bilgileri */}
                    <div className="col-12 lg:col-6">
                        <div>
                            <div className="card-header" style={{ background: '#273A81', color: 'white', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0' }}>
                                <div className="flex align-items-center">
                                    <i className="pi pi-warehouse mr-3" style={{ fontSize: '1.25rem' }}></i>
                                    <h6 className="m-0 font-semibold" style={{ color: 'white' }}>
                                        {th('dealerPartsReturnProcess.detailView.warehouseAndBranchInfo')}
                                    </h6>
                                </div>
                            </div>
                            <div className="card-body" style={{ padding: '0.5rem' }}>
                                <div className="grid">
                                    <div className="col-12">
                                        <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef', marginBottom: '0.3rem' }}>
                                            <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                                <i className="pi pi-building mr-2" style={{ color: '#5D72B2' }}></i>
                                                {th('dealerPartsReturnProcess.detailView.warehouseName')}
                                            </label>
                                            <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0' }}>
                                                {data.warehouseName || '-'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef', marginBottom: '0.3rem' }}>
                                            <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                                <i className="pi pi-sitemap mr-2" style={{ color: '#5D72B2' }}></i>
                                                {th('dealerPartsReturnProcess.detailView.bosBranchCode')}
                                            </label>
                                            <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0' }}>
                                                {data.bosBranchCode || '-'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                            <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                                <i className="pi pi-user mr-2" style={{ color: '#5D72B2' }}></i>
                                                {th('dealerPartsReturnProcess.detailView.dealerInfo')}
                                            </label>
                                            <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0' }}>
                                                {data.dealerInfo || '-'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* İade Talep Durumu ve Açıklamalar - Yan Yana */}
                <div className="grid">
                    {/* Eklenen Dökümanlar */}
                    {data.returnRequestId && (
                        <div className="col-12 lg:col-6">
                            <div className="card-header" style={{ background: '#273A81', color: 'white', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0' }}>
                                <div className="flex align-items-center">
                                    <i className="pi pi-paperclip mr-3" style={{ fontSize: '1.25rem' }}></i>
                                    <h6 className="m-0 font-semibold" style={{ color: 'white' }}>
                                        {th('dealerPartsReturnProcess.detailView.documentsTitle')}
                                    </h6>
                                </div>
                            </div>
                            <div className="card-body" style={{ padding: '0.5rem' }}>
                                {documentsError ? (
                                    <div className="flex align-items-center justify-content-center p-4" style={{ background: '#fee', color: '#c53030', borderRadius: '8px', border: '1px solid #feb2b2' }}>
                                        <i className="pi pi-exclamation-triangle mr-2" style={{ fontSize: '1.25rem' }}></i>
                                        <span className="font-medium">{th('dealerPartsReturnProcess.detailView.documentsLoadError')}</span>
                                    </div>
                                ) : isLoadingDocuments ? (
                                    <div className="text-center p-6">
                                        <div className="inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px', background: '#f0f9ff', borderRadius: '50%' }}>
                                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem', color: '#273A81' }}></i>
                                        </div>
                                        <div style={{ color: '#64748b', fontSize: '1rem' }}>{th('dealerPartsReturnProcess.detailView.documentsLoading')}</div>
                                    </div>
                                ) : documentsData.length === 0 ? (
                                    <div className="text-center p-2">
                                        <div className="inline-flex align-items-center justify-content-center mb-4" style={{ width: '80px', height: '80px', background: '#f8fafc', borderRadius: '50%', border: '2px dashed #cbd5e1' }}>
                                            <i className="pi pi-file-o" style={{ fontSize: '2rem', color: '#94a3b8' }}></i>
                                        </div>
                                        <div style={{ color: '#475569', fontSize: '1.1rem', fontWeight: '500', marginBottom: '0.5rem' }}>{th('dealerPartsReturnProcess.detailView.noDocumentsTitle')}</div>
                                        <div style={{ color: '#64748b', fontSize: '0.9rem' }}>{th('dealerPartsReturnProcess.detailView.noDocumentsMessage')}</div>
                                    </div>
                                ) : (
                                    <div className="grid w-full">
                                        {documentsData.map((document, index) => {
                                            const fileName = document.fileName ? document.fileName.split('/').pop() : 'Dosya';
                                            const fileSize = document.fileSize ? (document.fileSize / 1024 < 1024 ? `${(document.fileSize / 1024).toFixed(1)} KB` : `${(document.fileSize / 1024 / 1024).toFixed(1)} MB`) : '';

                                            // Dosya tipine göre ikon belirleme
                                            const getFileIcon = (fileType) => {
                                                const type = fileType?.toLowerCase();
                                                if (type?.includes('pdf')) return 'pi pi-file-pdf';
                                                if (type?.includes('image') || type?.includes('jpg') || type?.includes('png')) return 'pi pi-image';
                                                if (type?.includes('word') || type?.includes('doc')) return 'pi pi-file-word';
                                                if (type?.includes('excel') || type?.includes('xls')) return 'pi pi-file-excel';
                                                return 'pi pi-file';
                                            };

                                            const getFileColor = (fileType) => {
                                                const type = fileType?.toLowerCase();
                                                if (type?.includes('pdf')) return '#ef4444';
                                                if (type?.includes('image') || type?.includes('jpg') || type?.includes('png')) return '#10b981';
                                                if (type?.includes('word') || type?.includes('doc')) return '#3b82f6';
                                                if (type?.includes('excel') || type?.includes('xls')) return '#10b981';
                                                return '#6b7280';
                                            };

                                            return (
                                                <div key={document.id || index} className="col-12 ">
                                                    <div
                                                        className="document-card"
                                                        style={{
                                                            background: 'white',
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '12px',
                                                            padding: '1.25rem',
                                                            height: '100%',
                                                            transition: 'all 0.2s ease-in-out',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                                            e.currentTarget.style.borderColor = '#273A81';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                                        }}
                                                    >
                                                        <div className="flex align-items-start mb-3">
                                                            <div
                                                                className="flex align-items-center justify-content-center mr-3"
                                                                style={{
                                                                    width: '48px',
                                                                    height: '48px',
                                                                    background: 'rgba(39, 58, 129, 0.1)',
                                                                    borderRadius: '12px',
                                                                    flexShrink: 0
                                                                }}
                                                            >
                                                                <i
                                                                    className={getFileIcon(document.fileType)}
                                                                    style={{
                                                                        fontSize: '1.5rem',
                                                                        color: getFileColor(document.fileType)
                                                                    }}
                                                                ></i>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div
                                                                    className="font-semibold text-900 mb-1"
                                                                    style={{
                                                                        fontSize: '0.95rem',
                                                                        lineHeight: '1.3',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                    title={fileName}
                                                                >
                                                                    {fileName}
                                                                </div>
                                                                {document.title && (
                                                                    <div
                                                                        className="text-600 mb-1"
                                                                        style={{
                                                                            fontSize: '0.85rem',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap'
                                                                        }}
                                                                        title={document.title}
                                                                    >
                                                                        {document.title}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="flex align-items-center justify-content-between mb-3">
                                                            <div className="flex align-items-center gap-2">
                                                                {document.fileType && (
                                                                    <span
                                                                        className="inline-flex align-items-center px-2 py-1 border-round text-xs font-medium"
                                                                        style={{
                                                                            background: 'rgba(39, 58, 129, 0.1)',
                                                                            color: '#273A81',
                                                                            border: '1px solid rgba(39, 58, 129, 0.2)'
                                                                        }}
                                                                    >
                                                                        {document.fileType.toUpperCase()}
                                                                    </span>
                                                                )}
                                                                {fileSize && (
                                                                    <span className="text-600" style={{ fontSize: '0.8rem' }}>
                                                                        {fileSize}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="flex align-items-center justify-content-between">
                                                            {document.createdDate && (
                                                                <div className="flex align-items-center text-500" style={{ fontSize: '0.8rem' }}>
                                                                    <i className="pi pi-calendar mr-1"></i>
                                                                    {formatDateOnly(document.createdDate)}
                                                                </div>
                                                            )}

                                                            <Button
                                                                icon="pi pi-download"
                                                                className="p-button-sm p-button-rounded"
                                                                style={{
                                                                    background: '#273A81',
                                                                    border: 'none',
                                                                    padding: '0.5rem'
                                                                }}
                                                                onClick={(e) => handleDownloadDocument(document, e)}
                                                                loading={isDownloadingDocument}
                                                                title={th('dealerPartsReturnProcess.detailView.downloadFile')}
                                                                tooltip={th('dealerPartsReturnProcess.detailView.downloadFile')}
                                                                tooltipOptions={{ position: 'top' }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Sağ Kolon - Açıklamalar */}
                    <div className="col-12 lg:col-6">
                        <div>
                            <div className="card-header" style={{ background: '#273A81', color: 'white', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0' }}>
                                <div className="flex align-items-center">
                                    <i className="pi pi-file-text mr-3" style={{ fontSize: '1.25rem' }}></i>
                                    <h6 className="m-0 font-semibold" style={{ color: 'white' }}>
                                        {th('dealerPartsReturnProcess.detailView.descriptionsTitle')}
                                    </h6>
                                </div>
                            </div>
                            <div className="card-body" style={{ padding: '0.5rem' }}>
                                <div className="grid">
                                    {data.description && (
                                        <div className="col-12">
                                            <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef', marginBottom: '0.3rem' }}>
                                                <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                                    <i className="pi pi-info-circle mr-2" style={{ color: '#5D72B2' }}></i>
                                                    {th('dealerPartsReturnProcess.returnRequest.description')}
                                                </label>
                                                <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>
                                                    {data.description}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {data.lineDescription && (
                                        <div className="col-12">
                                            <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                                <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                                    <i className="pi pi-comment mr-2" style={{ color: '#5D72B2' }}></i>
                                                    {th('dealerPartsReturnProcess.detailView.lineDescription')}
                                                </label>
                                                <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>
                                                    {data.lineDescription}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {data.rejectionReason && (
                                        <div className="col-12">
                                            <div className="field-container" style={{ background: '#fff5f5', padding: '1rem', borderRadius: '8px', border: '1px solid #feb2b2' }}>
                                                <label className="field-label" style={{ color: '#c53030', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                                    <i className="pi pi-times-circle mr-2" style={{ color: '#c53030' }}></i>
                                                    {th('dealerPartsReturnProcess.rejectionReason')}
                                                </label>
                                                <p className="field-value" style={{ color: '#742a2a', fontSize: '1rem', fontWeight: '500', margin: '0', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>
                                                    {data.rejectionReason}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {!data.description && !data.lineDescription && !data.rejectionReason && (
                                        <div className="col-12">
                                            <div className="text-center p-4" style={{ color: '#64748b', fontStyle: 'italic' }}>
                                                {th('dealerPartsReturnProcess.detailView.noDescription')}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div>
                    <div>
                        <div className="card-header" style={{ background: '#273A81', color: 'white', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0' }}>
                            <div className="flex align-items-center">
                                <i className="pi pi-refresh mr-3" style={{ fontSize: '1.25rem' }}></i>
                                <h6 className="m-0 font-semibold" style={{ color: 'white' }}>
                                    {th('dealerPartsReturnProcess.detailView.returnRequestStatusTitle')}
                                </h6>
                            </div>
                        </div>
                        <div className="card-body" style={{ padding: '0.5rem' }}>
                            <div className="grid">
                                {/* Aktif */}
                                <div className="col-12 lg:col-4 md:col-6 sm:col-6">
                                    <div
                                        className="field-container"
                                        style={{ background: '#f8f9fa', padding: '0.8rem', borderRadius: '8px', border: '1px solid #e9ecef', height: '85px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                                    >
                                        <label
                                            className="field-label"
                                            style={{ color: '#273A81', fontWeight: '600', fontSize: '0.75rem', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', lineHeight: '1.2' }}
                                        >
                                            <i className="pi pi-flag mr-1" style={{ color: '#5D72B2', fontSize: '0.8rem' }}></i>
                                            {th('dealerPartsReturnProcess.detailView.active')}
                                        </label>
                                        <div className="field-value flex align-items-center justify-content-center">
                                            <span className={`inline-flex align-items-center px-2 py-1 border-round text-xs font-medium ${data.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                <i className={`pi ${data.isActive ? 'pi-check' : 'pi-times'} mr-1`} style={{ fontSize: '0.7rem' }}></i>
                                                {data.isActive ? th('dealerPartsReturnProcess.detailView.yes') : th('dealerPartsReturnProcess.detailView.no')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                {/* İade Talep ID */}
                                <div className="col-12 lg:col-4 md:col-6 sm:col-6">
                                    <div
                                        className="field-container"
                                        style={{ background: '#f8f9fa', padding: '0.8rem', borderRadius: '8px', border: '1px solid #e9ecef', height: '85px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                                    >
                                        <label
                                            className="field-label"
                                            style={{ color: '#273A81', fontWeight: '600', fontSize: '0.75rem', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', lineHeight: '1.2' }}
                                        >
                                            <i className="pi pi-id-card mr-1" style={{ color: '#5D72B2', fontSize: '0.8rem' }}></i>
                                            {th('dealerPartsReturnProcess.returnRequestId')}
                                        </label>
                                        <p className="field-value" style={{ color: '#2c3e50', fontSize: '0.8rem', fontWeight: '500', margin: '0', textAlign: 'center' }}>
                                            {data.returnRequestId || '-'}
                                        </p>
                                    </div>
                                </div>
                                {/* İade Talep Durumu */}
                                <div className="col-12 lg:col-4 md:col-6 sm:col-6">
                                    <div
                                        className="field-container"
                                        style={{ background: '#f8f9fa', padding: '0.8rem', borderRadius: '8px', border: '1px solid #e9ecef', height: '85px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                                    >
                                        <label
                                            className="field-label"
                                            style={{ color: '#273A81', fontWeight: '600', fontSize: '0.75rem', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', lineHeight: '1.2' }}
                                        >
                                            <i className="pi pi-clock mr-1" style={{ color: '#5D72B2', fontSize: '0.8rem' }}></i>
                                            {th('dealerPartsReturnProcess.detailView.returnRequestStatus')}
                                        </label>
                                        <p className="field-value" style={{ color: '#2c3e50', fontSize: '0.8rem', fontWeight: '500', margin: '0', textAlign: 'center' }}>
                                            {getReturnRequestStatusText(data.returnRequestStatus)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Arızalı Parça Detayları */}
                {data.isFaultyPart && (
                    <div>
                        <div className="card-header" style={{ background: '#273A81', color: 'white', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0' }}>
                            <div className="flex align-items-center">
                                <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '1.25rem' }}></i>
                                <h6 className="m-0 font-semibold" style={{ color: 'white' }}>
                                    {th('dealerPartsReturnProcess.detailView.faultyPartDetailsTitle')}
                                </h6>
                            </div>
                        </div>
                        <div className="card-body" style={{ padding: '0.5rem' }}>
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                        <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                            <i className="pi pi-car mr-2" style={{ color: '#5D72B2' }}></i>
                                            {th('dealerPartsReturnProcess.detailView.vinNo')}
                                        </label>
                                        <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0' }}>
                                            {data.vinno || '-'}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                        <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                            <i className="pi pi-calendar-plus mr-2" style={{ color: '#5D72B2' }}></i>
                                            {th('dealerPartsReturnProcess.detailView.assemblyDateLabel')}
                                        </label>
                                        <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0' }}>
                                            {data.assemblyDate ? formatDateOnly(data.assemblyDate) : '-'}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                        <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                            <i className="pi pi-gauge mr-2" style={{ color: '#5D72B2' }}></i>
                                            {th('dealerPartsReturnProcess.detailView.assemblyKmLabel')}
                                        </label>
                                        <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0' }}>
                                            {data.assemblyKm ? data.assemblyKm.toLocaleString('tr-TR') + ' ' + th('dealerPartsReturnProcess.detailView.kmUnit') : '-'}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                        <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                            <i className="pi pi-calendar-times mr-2" style={{ color: '#5D72B2' }}></i>
                                            {th('dealerPartsReturnProcess.detailView.faultDateLabel')}
                                        </label>
                                        <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0' }}>
                                            {data.faultDate ? formatDateOnly(data.faultDate) : '-'}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                        <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                            <i className="pi pi-gauge mr-2" style={{ color: '#5D72B2' }}></i>
                                            {th('dealerPartsReturnProcess.detailView.faultKmLabel')}
                                        </label>
                                        <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0' }}>
                                            {data.faultKm ? data.faultKm.toLocaleString('tr-TR') + ' ' + th('dealerPartsReturnProcess.detailView.kmUnit') : '-'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Onaya Gönderilenler tabında bayiler için İade Talebinden Vazgeç butonu */}
                {activeIndex === 1 && data.returnRequestId && hasDealerRole() && (
                    <div className="flex justify-content-end mt-4 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                        <Button
                            label={th('dealerPartsReturnProcess.actions.cancelRequest')}
                            icon="pi pi-times"
                            onClick={() => onCancelRequest?.(data.returnRequestId)}
                            loading={isCancellingRequest}
                            className="p-button-danger"
                            style={{ width: '220px' }}
                        />
                    </div>
                )}
                {/* Onaya Gönder butonu - görünürlük yalnızca backend'in canSendToApproval bayrağına bağlı */}
                {data.returnRequestId && data.canSendToApproval === true && (
                    <div className="flex justify-content-end mt-4 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                        <Button label={th('dealerPartsReturnProcess.actions.sendToRM')} icon="pi pi-send" onClick={() => onSendToRM?.(data.returnRequestId)} loading={isSendingToRM} style={{ width: '180px' }} />
                    </div>
                )}
                {/* Onaya Gönderilenler tabında onay yetkisi olan roller için Onayla/Reddet butonları */}
                {activeIndex === 1 && data.returnRequestId && (hasWarrantyTeamRole() || hasKarsanLogisticsRole() || hasAdminRole()) && (
                    <div className="flex justify-content-end mt-4 pt-3 w-full" style={{ borderTop: '1px solid #e9ecef' }}>
                        <ButtonGroup
                            buttons={[
                                {
                                    label: th('dealerPartsReturnProcess.actions.reject'),
                                    icon: 'pi pi-times',
                                    onClick: () => onRejectRequest?.(data.returnRequestId),
                                    severity: 'danger',
                                    disabled: isApproving || isRejecting
                                },
                                {
                                    label: th('dealerPartsReturnProcess.actions.approve'),
                                    icon: 'pi pi-check',
                                    onClick: () => onApproveRequest?.(data.returnRequestId),
                                    severity: 'success',
                                    disabled: isApproving || isRejecting
                                }
                            ]}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default DealerPartsDetailView;
